/**
 * Pagina /clienti/:id — dettaglio cliente con tab + breadcrumb di navigazione.
 *
 * Tab disponibili:
 *  - Anagrafica (dati anagrafici del cliente)
 *  - Siti (luoghi di installazione, hash #siti)
 *  - Contatti (destinatari notifiche, hash #contatti)
 *  - Forniture (consegne/deployments al cliente, hash #forniture)
 *
 * La tab attiva è persistita nell'hash della URL (/clienti/<id>#<slug>)
 * così la navigazione è condivisibile e sopravvive al refresh.
 */

import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useCustomer } from './hooks.js';
import { SitesSection } from './SitesSection.js';
import { ContactsSection } from './ContactsSection.js';
import { CustomerDeploymentsSection } from './CustomerDeploymentsSection.js';
import styles from './Customers.module.css';

type Tab = 'anagrafica' | 'siti' | 'contatti' | 'forniture';

/**
 * Tab del dettaglio cliente. `slug` è il frammento persistito nella URL
 * (`/customers/<id>#<slug>`) così la navigazione è condivisibile.
 */
const TABS = [
  { id: 'anagrafica', label: 'Anagrafica', slug: 'anagrafica' },
  { id: 'siti', label: 'Siti', slug: 'siti' },
  { id: 'contatti', label: 'Contatti', slug: 'contatti' },
  { id: 'forniture', label: 'Forniture', slug: 'forniture' },
] satisfies { id: Tab; label: string; slug: string }[];

/** Ricava la tab attiva dall'hash della URL; fallback ad 'anagrafica' se assente/sconosciuto. */
function tabFromHash(hash: string): Tab {
  const slug = hash.replace(/^#/, '');
  return TABS.find((t) => t.slug === slug)?.id ?? 'anagrafica';
}

/** Pannello anagrafica del cliente. */
function Anagrafica({ customerId }: { customerId: number }) {
  const { data: cliente, isLoading, isError, error } = useCustomer(customerId);

  if (isLoading) {
    return (
      <p className={styles.loadingMsg} data-testid="loading-cliente">
        Caricamento cliente…
      </p>
    );
  }

  if (isError) {
    return (
      <p className={styles.errorMsg} data-testid="errore-cliente">
        {error instanceof Error ? error.message : 'Errore nel caricamento del cliente'}
      </p>
    );
  }

  if (!cliente) return null;

  return (
    <div className={styles.anagrafica} data-testid="anagrafica-cliente">
      <div className={styles.anagraficaItem}>
        <span className={styles.anagraficaLabel}>Nome</span>
        <span className={styles.anagraficaValue}>{cliente.name}</span>
      </div>
      {cliente.externalRef && (
        <div className={styles.anagraficaItem}>
          <span className={styles.anagraficaLabel}>Riferimento esterno</span>
          <span className={styles.anagraficaValue}>{cliente.externalRef}</span>
        </div>
      )}
      {cliente.websiteUrl && (
        <div className={styles.anagraficaItem}>
          <span className={styles.anagraficaLabel}>Sito web</span>
          <span className={styles.anagraficaValue}>
            <a href={cliente.websiteUrl} target="_blank" rel="noreferrer">
              {cliente.websiteUrl}
            </a>
          </span>
        </div>
      )}
      <div className={styles.anagraficaItem}>
        <span className={styles.anagraficaLabel}>Creato il</span>
        <span className={styles.anagraficaValue}>{cliente.createdAt}</span>
      </div>
    </div>
  );
}

/** Pagina dettaglio cliente con breadcrumb + navigazione a tab. */
export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // La tab attiva è derivata dall'hash: unica fonte di verità, niente stato duplicato.
  const tabAttiva = tabFromHash(location.hash);
  // Cliente caricato a livello pagina per il breadcrumb (nome); l'Anagrafica tab
  // ha la sua istanza di useCustomer (cache condivisa via queryKey → 1 fetch).
  const { data: cliente } = useCustomer(Number(id ?? 0));

  // id è garantito dalla route, ma lo gestiamo difensivamente
  if (!id) {
    return (
      <p className={styles.errorMsg} data-testid="errore-id-mancante">
        ID cliente mancante nella URL.
      </p>
    );
  }

  const customerId = Number(id);

  if (!Number.isInteger(customerId) || customerId <= 0) {
    return (
      <p className={styles.errorMsg} data-testid="errore-id-non-valido">
        ID cliente non valido: {id}
      </p>
    );
  }

  return (
    <div data-testid="customer-detail-page">
      {/* Breadcrumb: Clienti / <nome cliente> */}
      <nav className={styles.breadcrumb} aria-label="breadcrumb" data-testid="breadcrumb-cliente">
        <button
          className={styles.breadcrumbLink}
          onClick={() => navigate('/customers')}
          data-testid="breadcrumb-clienti"
        >
          Clienti
        </button>
        <span className={styles.breadcrumbSep} aria-hidden="true">
          /
        </span>
        <span className={styles.breadcrumbCurrent} data-testid="breadcrumb-cliente-corrente">
          {cliente?.name ?? `Cliente #${id}`}
        </span>
      </nav>

      {/* Tab navigation — il click aggiorna l'hash della URL (replace: niente spam nella history) */}
      <div className={styles.tabs} role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={tabAttiva === tab.id}
            className={`${styles.tab} ${tabAttiva === tab.id ? styles.tabActive : ''}`}
            onClick={() => navigate(`${location.pathname}#${tab.slug}`, { replace: true })}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenuto tab attiva */}
      <div role="tabpanel" data-testid={`contenuto-tab-${tabAttiva}`}>
        {tabAttiva === 'anagrafica' && <Anagrafica customerId={customerId} />}
        {tabAttiva === 'siti' && <SitesSection customerId={customerId} />}
        {tabAttiva === 'contatti' && <ContactsSection customerId={customerId} />}
        {tabAttiva === 'forniture' && <CustomerDeploymentsSection customerId={customerId} />}
      </div>
    </div>
  );
}
